import { ProtocolImage } from '@/components/common/protocolImage'
import { RoundImage } from '@/components/common/roundImage'
import { useMixpanel } from '@/context/mixpanel'
import { useFixedTermMarketsSearchState } from '@/hooks/useFixedTermMarketsSearchState'
import type { FixedTermMarket } from '@/utils/internalAppSdk'
import { getNetworkName } from '@/utils/network/getNetworkName'
import { getProtocolDisplayName } from '@/utils/protocol'
import { getNetworkImage, getTokenImage } from '@vaultsfyi/common/utils'
import { useMemo } from 'react'

export const useFixedTermMarketsFilterData = (markets: FixedTermMarket[]) => {
  const { trackEvent } = useMixpanel()
  const {
    assets: selectedAssets,
    setAssets,
    protocols: selectedProtocols,
    setProtocols,
    networks: selectedNetworks,
    setNetworks,
  } = useFixedTermMarketsSearchState()
  const assets = useMemo(
    () =>
      [...new Set(markets.flatMap((market) => market.accountingAsset.symbol ?? []))].sort((a, b) => a.localeCompare(b)),
    [markets]
  )
  const protocols = useMemo(
    () => [...new Set(markets.map((market) => market.underlyingProtocol.name))].sort((a, b) => a.localeCompare(b)),
    [markets]
  )
  const protocolDisplayNames = useMemo(
    () =>
      Object.fromEntries(
        markets.map((market) => [market.underlyingProtocol.name, getProtocolDisplayName(market.underlyingProtocol)])
      ),
    [markets]
  )
  const networks = useMemo(
    () =>
      [...new Set(markets.map((market) => market.network.name))].sort((a, b) =>
        getNetworkName(a).localeCompare(getNetworkName(b))
      ),
    [markets]
  )

  return {
    assetFilterProps: {
      items: assets,
      selectedItems: selectedAssets,
      setSelectedItems: (value: string[]) => {
        setAssets(value)
        trackEvent('Filter used', {
          Filter: 'asset',
          Value: value.join(', '),
          Page: 'Discover',
          'Opportunity type': 'Fixed term',
        })
      },
      name: 'asset',
      renderItemPrefix: (item: string) => (
        <RoundImage
          src={getTokenImage(item)}
          fallback={getTokenImage('fallback')}
          alt={`${item} token`}
          className="size-7"
        />
      ),
    },
    protocolFilterProps: {
      items: protocols,
      selectedItems: selectedProtocols,
      setSelectedItems: (value: string[]) => {
        setProtocols(value)
        trackEvent('Filter used', {
          Filter: 'underlying protocol',
          Value: value.join(', '),
          Page: 'Discover',
          'Opportunity type': 'Fixed term',
        })
      },
      name: 'underlying protocol',
      renderItemPrefix: (item: string) => (
        <ProtocolImage protocol={{ name: item, displayName: protocolDisplayNames[item] }} className="size-7" />
      ),
      itemFormatter: (item: string) => protocolDisplayNames[item] ?? item,
      searchNormalizer: (item: string) => `${item} ${protocolDisplayNames[item]?.toLowerCase() ?? ''}`,
    },
    networkFilterProps: {
      items: networks,
      selectedItems: selectedNetworks,
      setSelectedItems: (value: string[]) => {
        setNetworks(value)
        trackEvent('Filter used', {
          Filter: 'network',
          Value: value.join(', '),
          Page: 'Discover',
          'Opportunity type': 'Fixed term',
        })
      },
      name: 'network',
      renderItemPrefix: (item: string) => (
        <RoundImage
          src={getNetworkImage(item)}
          fallback={getNetworkImage('fallback')}
          alt={`${item} network`}
          className="size-7"
        />
      ),
      searchNormalizer: (item: string) => `${item} ${getNetworkName(item).toLowerCase()}`,
      itemFormatter: getNetworkName,
      capitalize: true,
    },
  }
}
